import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import DeleteIcon from '@mui/icons-material/Delete';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-location';
import { useEffect, useState } from 'react';
import { apiRawFetch } from '../../main/client';
import SaveableField from '../components/SaveableField';
import { usePropertySettings } from './PropertySettingsContext';

interface TaxPresetSummary {
    id: string;
    name: string;
    country: string;
    entityType: 'individual' | 'company';
    description: string;
}

const COUNTRIES_QUERY = gql`
    query countries {
        countries {
            code
            name
        }
    }
`;

const COMMON_CURRENCIES = [
    'EUR',
    'USD',
    'GBP',
    'HUF',
    'JPY',
    'CHF',
    'CAD',
    'AUD',
    'CNY',
    'SEK',
    'NOK',
    'DKK',
    'PLN',
    'CZK',
];

const textFieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 0,
        '& input, & textarea, & .MuiSelect-select': {
            backgroundColor: '#0f0f23',
        },
        '& fieldset': {
            borderWidth: '2px',
            borderColor: '#374151',
        },
        '&:hover fieldset': {
            borderWidth: '2px',
            borderColor: '#6366f1',
        },
        '&.Mui-focused fieldset': {
            borderWidth: '3px',
            borderColor: '#6366f1',
        },
    },
    '& .MuiInputLabel-root': {
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '1rem',
        letterSpacing: '0.05em',
    },
};

const GeneralSettingsPage = () => {
    const navigate = useNavigate();
    const { property, refreshProperty } = usePropertySettings();

    // Field states
    const [name, setName] = useState(property.name);
    const [country, setCountry] = useState(property.country);
    const [minNights, setMinNights] = useState(property.minNights.toString());
    const [currency, setCurrency] = useState(property.currency);
    const [weekStartDay, setWeekStartDay] = useState<0 | 1>(
        property.weekStartDay
    );
    const [taxPresetId, setTaxPresetId] = useState<string | null>(
        property.taxPresetId
    );
    const [taxPresets, setTaxPresets] = useState<TaxPresetSummary[]>([]);

    // Delete state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // Success snackbar
    const [successOpen, setSuccessOpen] = useState(false);

    const { data: countriesData } = useQuery<{
        countries: { code: string; name: string }[];
    }>(COUNTRIES_QUERY);

    // Fetch available tax presets
    useEffect(() => {
        const fetchPresets = async () => {
            try {
                const response = await apiRawFetch('api/tax-presets');
                if (response.ok) {
                    const { data } = await response.json();
                    setTaxPresets(data);
                }
            } catch (error) {
                console.error('Failed to fetch tax presets', error);
            }
        };
        fetchPresets();
    }, []);

    const countries = countriesData?.countries ?? [];
    const sortedCountries = countries
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name));

    const saveField = async (fieldName: string, value: unknown) => {
        const response = await apiRawFetch(
            `api/properties/${property.id}`,
            'POST',
            {
                [fieldName]: value,
            }
        );

        if (!response.ok) {
            const data = (await response.json().catch(() => ({}))) as {
                error?: string;
            };
            throw new Error(data.error ?? 'Failed to save');
        }

        await refreshProperty();
        setSuccessOpen(true);
    };

    const handleSaveName = async () => {
        if (!name.trim()) {
            throw new Error('Property name is required');
        }
        await saveField('name', name.trim());
    };

    const handleSaveCountry = async () => {
        if (!country || country.length !== 2) {
            throw new Error('Country is required');
        }
        await saveField('country', country.toUpperCase());
    };

    const handleSaveMinNights = async () => {
        const minNightsNum = parseInt(minNights, 10);
        if (isNaN(minNightsNum) || minNightsNum < 1) {
            throw new Error('Minimum nights must be at least 1');
        }
        await saveField('minNights', minNightsNum);
    };

    const handleSaveCurrency = async () => {
        if (!currency || currency.length !== 3) {
            throw new Error('Currency must be exactly 3 characters');
        }
        await saveField('currency', currency.toUpperCase());
    };

    const handleSaveWeekStartDay = async () => {
        await saveField('weekStartDay', weekStartDay);
    };

    const handleSaveTaxPreset = async () => {
        await saveField('taxPresetId', taxPresetId);
    };

    const handleDelete = async () => {
        try {
            setDeleting(true);
            setDeleteError(null);

            const response = await apiRawFetch(
                `api/properties/${property.id}/delete`,
                'POST'
            );

            if (!response.ok) {
                const data = (await response.json().catch(() => ({}))) as {
                    error?: string;
                };
                throw new Error(data.error ?? 'Failed to delete');
            }

            navigate({ to: '/properties' });
        } catch (err) {
            setDeleteError(
                err instanceof Error ? err.message : 'Unknown error'
            );
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <Stack spacing={3}>
                <SaveableField
                    originalValue={property.name}
                    currentValue={name}
                    onSave={handleSaveName}
                >
                    <TextField
                        label="Property Name"
                        type="text"
                        fullWidth
                        value={name}
                        onChange={e => setName(e.target.value)}
                        required
                        sx={textFieldSx}
                    />
                </SaveableField>

                <SaveableField
                    originalValue={property.country}
                    currentValue={country}
                    onSave={handleSaveCountry}
                >
                    <Autocomplete
                        value={
                            sortedCountries.find(c => c.code === country) ??
                            null
                        }
                        onChange={(_event, newValue) => {
                            if (newValue) {
                                setCountry(newValue.code);
                            }
                        }}
                        options={sortedCountries}
                        getOptionLabel={option =>
                            `${option.name} (${option.code})`
                        }
                        isOptionEqualToValue={(option, value) =>
                            option.code === value.code
                        }
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Country"
                                required
                                sx={textFieldSx}
                            />
                        )}
                    />
                </SaveableField>

                <SaveableField
                    originalValue={property.minNights.toString()}
                    currentValue={minNights}
                    onSave={handleSaveMinNights}
                >
                    <TextField
                        label="Minimum Nights"
                        type="number"
                        fullWidth
                        value={minNights}
                        onChange={e => setMinNights(e.target.value)}
                        required
                        slotProps={{
                            htmlInput: {
                                min: 1,
                            },
                        }}
                        sx={textFieldSx}
                    />
                </SaveableField>

                <SaveableField
                    originalValue={property.currency}
                    currentValue={currency}
                    onSave={handleSaveCurrency}
                >
                    <Autocomplete
                        freeSolo
                        value={currency}
                        onChange={(_event, newValue) => {
                            if (newValue && typeof newValue === 'string') {
                                setCurrency(newValue.toUpperCase());
                            }
                        }}
                        onInputChange={(_event, newValue) => {
                            setCurrency(newValue.toUpperCase());
                        }}
                        options={COMMON_CURRENCIES}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Currency"
                                required
                                slotProps={{
                                    htmlInput: {
                                        ...params.inputProps,
                                        maxLength: 3,
                                    },
                                }}
                                sx={textFieldSx}
                            />
                        )}
                    />
                </SaveableField>

                <SaveableField
                    originalValue={property.weekStartDay}
                    currentValue={weekStartDay}
                    onSave={handleSaveWeekStartDay}
                >
                    <TextField
                        select
                        label="Week Starts On"
                        fullWidth
                        value={weekStartDay}
                        onChange={e =>
                            setWeekStartDay(
                                parseInt(e.target.value, 10) as 0 | 1
                            )
                        }
                        sx={textFieldSx}
                    >
                        <MenuItem value={1}>Monday (ISO 8601)</MenuItem>
                        <MenuItem value={0}>Sunday</MenuItem>
                    </TextField>
                </SaveableField>

                <SaveableField
                    originalValue={property.taxPresetId ?? ''}
                    currentValue={taxPresetId ?? ''}
                    onSave={handleSaveTaxPreset}
                >
                    <TextField
                        select
                        label="Tax Profile"
                        fullWidth
                        value={taxPresetId ?? ''}
                        onChange={e => setTaxPresetId(e.target.value || null)}
                        sx={textFieldSx}
                        helperText="Select a tax profile to see tax estimates on your financial dashboard"
                    >
                        <MenuItem value="">
                            <em>Not configured</em>
                        </MenuItem>
                        {taxPresets.map(preset => (
                            <MenuItem key={preset.id} value={preset.id}>
                                {preset.name} ({preset.country} -{' '}
                                {preset.entityType})
                            </MenuItem>
                        ))}
                    </TextField>
                </SaveableField>

                {/* Danger Zone */}
                <Paper
                    sx={{
                        mt: 4,
                        p: 3,
                        borderRadius: 0,
                        border: '2px solid #ef4444',
                        backgroundColor: 'rgba(239, 68, 68, 0.05)',
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            color: '#ef4444',
                            mb: 2,
                        }}
                    >
                        Danger Zone
                    </Typography>
                    {deleteError && (
                        <Alert
                            severity="error"
                            onClose={() => setDeleteError(null)}
                            sx={{
                                mb: 2,
                                borderRadius: 0,
                                border: '2px solid #ef4444',
                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                fontWeight: 600,
                                '& .MuiAlert-icon': {
                                    color: '#ef4444',
                                },
                            }}
                        >
                            {deleteError}
                        </Alert>
                    )}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box>
                            <Typography fontWeight={600}>
                                Delete Property
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Permanently delete this property and all its
                                data
                            </Typography>
                        </Box>
                        <Button
                            onClick={() => setDeleteDialogOpen(true)}
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            sx={{
                                borderRadius: 0,
                                borderWidth: '2px',
                                borderColor: 'error.main',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                px: 3,
                                py: 1,
                                '&:hover': {
                                    borderWidth: '2px',
                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                },
                            }}
                        >
                            Delete
                        </Button>
                    </Box>
                </Paper>
            </Stack>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => !deleting && setDeleteDialogOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: 0,
                        border: '4px solid #ef4444',
                        backgroundColor: '#1a1a2e',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        borderBottom: '3px solid #ef4444',
                        backgroundColor: '#0f0f23',
                        fontWeight: 900,
                        fontSize: '1.5rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 2.5,
                        color: '#ef4444',
                    }}
                >
                    Delete Property
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: '#1a1a2e', p: 0 }}>
                    <Stack sx={{ py: 3, px: 3 }} spacing={2}>
                        <Typography>
                            Are you sure you want to delete{' '}
                            <strong>{property.name}</strong>?
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            This action cannot be undone. All bookings, cleaning
                            schedules, and other data associated with this
                            property will be permanently deleted.
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        borderTop: '3px solid #374151',
                        backgroundColor: '#0f0f23',
                        p: 3,
                        gap: 1.5,
                    }}
                >
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        disabled={deleting}
                        variant="outlined"
                        sx={{
                            borderRadius: 0,
                            borderWidth: '2px',
                            borderColor: '#374151',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 3,
                            py: 1,
                            '&:hover': {
                                borderWidth: '2px',
                                borderColor: '#374151',
                                backgroundColor: 'rgba(55, 65, 81, 0.1)',
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        disabled={deleting}
                        variant="contained"
                        color="error"
                        sx={{
                            borderRadius: 0,
                            border: '2px solid',
                            borderColor: 'error.main',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 3,
                            py: 1,
                        }}
                    >
                        {deleting ? 'Deleting...' : 'Delete Property'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Snackbar */}
            <Snackbar
                open={successOpen}
                autoHideDuration={3000}
                onClose={() => setSuccessOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSuccessOpen(false)}
                    severity="success"
                    sx={{
                        borderRadius: 0,
                        border: '2px solid #22c55e',
                        backgroundColor: 'rgba(34, 197, 94, 0.1)',
                        fontWeight: 600,
                    }}
                >
                    Settings saved successfully
                </Alert>
            </Snackbar>
        </>
    );
};

export default GeneralSettingsPage;
